<!--
  Forbrukerrådet - Kommunetesten
  @link https://gitlab01.copyleft.no/FBR/kommunetesten-new
  @author Anders Evenrud <anders.evenrud@copyleft.no>
-->
<template>
  <div class="dropdown" role="button">
    <input type="hidden" :name="name" :value="inputValue" />

    <button
      class="dropdown__inner"
      type="button"
      aria-haspopup="listbox"
      @click="onActivate"
      @keydown.enter="onEnterPress"
      @keydown.down.prevent="onKeyDownPress"
      @keydown.up.prevent="onKeyUpPress"
    >
      <span>{{ dropdownLabel || placeholder }}</span>
      <font-awesome-icon :icon="dropdownIcon" />
    </button>

    <div class="dropdown__items" :class="dropdownClass" role="listbox" tabindex="-1">
      <ul>
        <li v-for="(item, index) in items" :key="item.value" :ref="itemRef(index)">
          <div @click="onClick($event, item)" :class="itemClass(item, index)">
            <span>{{ item.label }}</span>
          </div>
        </li>
      </ul>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@import "../assets/variables";
@import "../assets/mixins";

.dropdown {
  @include input();
  user-select: none;
  min-width: 12em;
  font-size: 88%;

  &__inner {
    min-height: 1em;
    box-sizing: border-box;
    cursor: pointer;
    display: flex;
    align-items: center;

    /* Reset button styles */
    margin: 0;
    padding: 0;
    -webkit-appearance: none;
    -moz-appearance: none;
    border: 0 none;
    width: 100%;
    text-align: left;
    font: inherit;
    background: transparent;

    &::-moz-focus-inner {
      padding: 0;
      border: 0
    }

    & > span {
      white-space: nowrap;
      display: block;
      line-height: normal;
      overflow: hidden;
      text-overflow: ellipsis;
      flex: 1 1;
      padding-right: 1em;
    }
  }

  &__items {
    @include popup();
    max-height: 10em;
    z-index: 8000;
    max-height: 60vh;
    overflow: auto;

    &--inactive {
      display: none;
    }

    ul {
      margin: 0;
      padding: 0;
      list-style-type: none;
    }

    li {
      padding: 0;
      margin: 0;

      & > div {
        padding: 0.6em;
        cursor: pointer;
        white-space: nowrap;

        &.active,
        &:hover {
          background-color: #F7FAFA;
        }
      }
    }
  }
}
</style>

<script>
import { EventBus } from '../bus.js'

export default {
  props: {
    placeholder: String,
    name: String,
    items: Array,
    value: String
  },

  data () {
    const foundIndex = this.items.findIndex(item => String(item.value) === this.value)

    return {
      selected: this.items[foundIndex],
      hovering: foundIndex,
      active: false
    }
  },

  created () {
    EventBus.$on('body-clicked', ev => {
      if (this.active) {
        const hit = ev.target === this.$el ||
          this.$el.contains(ev.target)

        if (!hit) {
          this.onActivate(ev, false)
        }
      }
    })
  },

  computed: {
    inputValue () {
      return this.selected ? this.selected.value : null
    },

    dropdownLabel () {
      return this.selected ? this.selected.label : null
    },

    dropdownClass () {
      return 'dropdown__items--' + (this.active ? 'active' : 'inactive')
    },

    dropdownIcon () {
      return this.active ? 'angle-up' : 'angle-down'
    },

    itemCount () {
      return this.items.length
    }
  },

  methods: {
    itemClass (item, index) {
      if (index === this.hovering) {
        return 'active'
      }

      if (this.hovering !== -1) {
        return ''
      }

      if (this.selected && this.selected.value === item.value) {
        return 'active'
      }

      return ''
    },

    itemRef (index) {
      return this.hovering === index ? 'hovering' : undefined
    },

    scrollIntoView () {
      if (!this.$refs.hovering) {
        return
      }

      this.$refs.hovering[0].scrollIntoView({
        inline: 'start',
        block: 'nearest'
      })
    },

    onClick (ev, item) {
      // NOTE: Do not select the first item as it always is a nullable
      this.selected = item.value ? item : null
      this.active = false
      this.hovering = -1

      this.$nextTick(() => this.$emit('onchange', ev, item))
    },

    onEnterPress (ev) {
      if (this.hovering !== -1) {
        this.onClick(ev, this.items[this.hovering])
      }
    },

    onKeyUpPress (ev) {
      if (this.itemCount > 0) {
        this.hovering = Math.max(0, this.hovering - 1)
        this.$nextTick(() => this.scrollIntoView())
      }
    },

    onKeyDownPress (ev) {
      if (this.itemCount > 0) {
        this.hovering = Math.min(this.itemCount - 1, this.hovering + 1)
        this.$nextTick(() => this.scrollIntoView())
      }
    },

    onActivate (ev, state) {
      this.active = typeof state === 'undefined'
        ? !this.active
        : state

      if (!this.active) {
        this.hovering = -1
      }

      this.$emit('onactivate', ev, this.active)
    }
  }
}
</script>
